import type { Column } from '@amzn/gft-ag-grid-enterprise';
import { GridApi, ICellRendererParams } from '@amzn/gft-ag-grid-enterprise';
import { NotificationType } from '@components/Common/Notification';
import { ScheduleType } from '@services/api/deckManagement/graphql/schema/API';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

/**
 * EditableCellRenderer Props Interface
 */
export interface EditableCellRendererProps<TData = EnhancedTOCEntry> extends ICellRendererParams<TData> {
  column: Column;
  isEditing?: (params: EditableCellParams<TData>) => boolean;
  isRenameMode?: (params: EditableCellParams<TData>) => boolean;
  onEndEdit?: (params: EditableCellParams<TData>) => void;
  onCellValueChange?: (params: CellValueChangeParams<TData>) => void;
  allowedInputPattern?: RegExp;
  minLength?: number;
  maxLength?: number;
}

/**
 * PageNameCellRenderer Props Interface
 */
export interface PageNameCellRendererProps<TData = EnhancedTOCEntry> extends ICellRendererParams<TData> {
  isEditing: (params: EditableCellParams<TData>) => boolean;
  onEndEdit: (params: EditableCellParams<TData>) => void;
  onCellValueChange: (params: CellValueChangeParams<TData>) => void;
  deckId: string;
}

export interface CellValueChangeParams<TData = EnhancedTOCEntry, TField extends keyof TData = keyof TData> {
  data: TData;
  field: TField;
  newValue: TData[TField];
}

/**
 * EditableCellParams Interface
 */
export interface EditableCellParams<TData = EnhancedTOCEntry> {
  data: TData;
  value: string;
  field: string;
  api: GridApi;
}

/**
 * ActionsCellRenderer Props Interface
 */
export interface TOCActionsCellRendererProps extends ICellRendererParams {
  /** Callback to toggle page visibility */
  onToggleVisibility?: (pageId: string) => void;
  /** Callback to start renaming a page field */
  onStartRename?: (pageId: string) => void;
  /** Callback to start editing */
  onStartSimultaneousEdit?: (pageId: string) => void;
  /** Callback to delete a page */
  onDelete?: (pageId: string, pageName: string) => void;
  /** Callback to display notifications to the user */
  onNotification?: (message: string, type: NotificationType) => void;
  /** Callback when a page is copied */
  onPageCopy?: (pageId: string) => void;
  /** Callback when a page is moved between active/inactive views */
  onMoveToInactive?: (pageId: string, isMovingToInactive: boolean) => void;
  /** Callback when a page is moved to active with schedule type */
  onMoveToActive?: (pageId: string, scheduleType: ScheduleType) => void;
  /** Current view (active/inactive) */
  currentView?: string;
  /** Whether editing is disabled due to deck lock */
  isEditingDisabled?: boolean;
}

export interface TOCGridContext {
  onCancelEdit: () => void;
  gridApi: GridApi | null;
}

/**
 * Enum for TOC Entry fields that need special formatting in audit history
 */
export enum TOCFormattingFields {
  IS_VISIBLE = 'is_visible',
  IS_ACTIVE = 'is_active',
}

/**
 * Mapping of TOC formatting fields to their user-friendly audit log labels
 * Used for audit history tracking
 */
export const TOC_FORMATTING_FIELD_AUDIT_LABELS: Record<TOCFormattingFields, string> = {
  [TOCFormattingFields.IS_VISIBLE]: 'Visible',
  [TOCFormattingFields.IS_ACTIVE]: 'Active',
};
